<script lang="ts">
	import { page } from '$app/state';
	import {
		LayoutDashboard,
		Server,
		Settings,
		Sun,
		Moon,
		MonitorCog,
		LogOut,
		Users,
		Search,
		RefreshCw,
		Skull
	} from '@lucide/svelte';
	import { auth } from '$lib/stores/auth.svelte';
	import { refreshBus } from '$lib/stores/refresh.svelte';
	import QuickControls from '$lib/components/QuickControls.svelte';
	import { createWsStore } from '$lib/ws.svelte';

	// `onsearch` opens the global Ctrl/Cmd+K command palette (owned by
	// +layout.svelte, which holds the `paletteOpen` state and passes this
	// callback down) -- before this, the palette existed but had no visible
	// entry point anywhere in the UI, only the keyboard shortcut itself
	// (undiscoverable). This pill is that entry point.
	let {
		theme,
		onthemechange,
		onsearch
	}: {
		theme: 'light' | 'dark' | null;
		onthemechange: (t: 'light' | 'dark' | null) => void;
		onsearch: () => void;
	} = $props();

	// Valheim et Palworld sont des serveurs comme les autres : pas d'entrée de
	// nav dédiée -- ils vivent dans la liste "Serveurs" au même titre que les
	// instances du catalogue (voir /servers et la grille de la Vue d'ensemble).
	const links = [
		{ href: '/', label: 'Vue d’ensemble', icon: LayoutDashboard },
		{ href: '/servers', label: 'Serveurs', icon: Server },
		{ href: '/last-asylum', label: 'Last Asylum', icon: Skull },
		{ href: '/system', label: 'Système', icon: Settings }
	];

	// Contrairement aux autres liens (jamais de sous-route), "Serveurs" a des
	// sous-pages (/servers/new, /servers/[slug]) qui doivent aussi surligner
	// l'entrée de nav -- égalité stricte uniquement sinon.
	function isCurrent(href: string) {
		const path = page.url.pathname;
		return path === href || path.startsWith(href + '/');
	}

	function cycleTheme() {
		const next = theme === null ? 'dark' : theme === 'dark' ? 'light' : null;
		onthemechange(next);
	}

	// Bouton "Rafraîchir" global : force un refetch immédiat de la vue
	// courante (voir ws.svelte.ts, qui écoute refreshBus.tick et reconnecte
	// son WebSocket) plutôt que d'attendre le prochain tick serveur. La
	// petite icône tourne brièvement pour confirmer visuellement le clic --
	// neutralisée par la règle prefers-reduced-motion globale comme toute
	// autre animation de l'app.
	let spinning = $state(false);
	function handleRefresh() {
		refreshBus.bump();
		spinning = true;
		setTimeout(() => (spinning = false), 600);
	}

	// Présence multi-admin : la sidebar est montée sur TOUTES les pages
	// (contrairement à la vue d'ensemble, qui n'ouvre son propre WS overview
	// que sur "/"), donc c'est l'endroit le plus fiable pour observer combien
	// d'onglets/admins ont le dashboard ouvert en ce moment, quelle que soit
	// la page consultée. `connections` vient du backend (ConnectionManager
	// existant, voir routers/overview.py) -- compte des WS /ws/overview
	// distincts, pas une vraie identité par admin (un même admin avec 2
	// onglets compte pour 2), mais c'est le proxy le plus simple disponible
	// sans ajouter d'état de session dédié. Aussi réutilisé ici comme
	// indicateur de connexion (presence.connected) pour le bouton Rafraîchir.
	const presence = createWsStore<{ connections: number }>('/ws/overview');
</script>

<nav class="sidebar">
	<div class="brand">
		<MonitorCog size={20} />
		<span>gamedash</span>
	</div>
	<button class="search-pill" onclick={onsearch} type="button">
		<Search size={15} />
		<span>Rechercher…</span>
		<kbd>⌘K</kbd>
	</button>
	<ul>
		{#each links as l (l.href)}
			<li>
				<a href={l.href} aria-current={isCurrent(l.href) ? 'page' : undefined}>
					<l.icon size={18} />
					<span>{l.label}</span>
				</a>
			</li>
		{/each}
	</ul>
	<div class="quick-wrap">
		<QuickControls />
	</div>
	<div class="bottom">
		<div class="conn-row">
			<span
				class="conn-dot"
				data-connected={presence.connected}
				title={presence.connected ? 'Connecté en direct' : 'Reconnexion…'}
				aria-hidden="true"
			></span>
			<button
				class="ghost-btn refresh-btn"
				onclick={handleRefresh}
				title="Rafraîchir maintenant"
				aria-label="Rafraîchir maintenant"
			>
				<span class="refresh-icon" class:spin={spinning}><RefreshCw size={16} /></span>
				<span>Rafraîchir</span>
			</button>
		</div>
		{#if presence.data && presence.data.connections > 1}
			<span
				class="presence"
				title="{presence.data.connections} sessions dashboard actuellement ouvertes"
			>
				<Users size={14} />
				<span>{presence.data.connections} connectés</span>
			</span>
		{/if}
		<button
			class="ghost-btn"
			onclick={cycleTheme}
			title="Changer de thème"
			aria-label="Changer de thème"
		>
			{#if theme === 'dark'}
				<Moon size={16} />
			{:else if theme === 'light'}
				<Sun size={16} />
			{:else}
				<MonitorCog size={16} />
			{/if}
			<span>{theme === 'dark' ? 'Sombre' : theme === 'light' ? 'Clair' : 'Auto'}</span>
		</button>
		{#if auth.user}
			<button
				class="ghost-btn"
				onclick={() => auth.logout()}
				title="Déconnexion"
				aria-label="Déconnexion"
			>
				<LogOut size={16} />
				<span>{auth.user}</span>
			</button>
		{/if}
	</div>
</nav>

<style>
	.sidebar {
		display: flex;
		flex-direction: column;
		width: 220px;
		flex-shrink: 0;
		background: var(--surface-1);
		border-right: 1px solid var(--border);
		padding: var(--space-4);
		gap: var(--space-6);
	}
	.brand {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-family: var(--font-shell-display);
		font-weight: 700;
		letter-spacing: var(--font-shell-tracking);
		color: var(--text-primary);
	}
	.search-pill {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		width: 100%;
		font: inherit;
		font-size: var(--text-sm);
		font-weight: 600;
		padding: var(--space-2) var(--space-3);
		border: 1px solid var(--border);
		border-radius: var(--radius-sm);
		background: var(--surface-2);
		color: var(--text-muted);
		cursor: pointer;
		transition:
			border-color var(--dur-fast) var(--ease-out),
			color var(--dur-fast) var(--ease-out);
	}
	.search-pill:hover {
		border-color: var(--accent);
		color: var(--accent);
	}
	.search-pill span {
		flex: 1;
		text-align: left;
	}
	.search-pill kbd {
		font-size: var(--text-xs);
		font-weight: 700;
		color: var(--text-muted);
		border: 1px solid var(--border);
		border-radius: var(--radius-sm);
		padding: 1px 5px;
	}
	ul {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}
	a {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		padding: var(--space-2) var(--space-3);
		border-radius: var(--radius-sm);
		color: var(--text-secondary);
		text-decoration: none;
		font-weight: 600;
		font-size: var(--text-sm);
		transition:
			border-color var(--dur-fast) var(--ease-out),
			color var(--dur-fast) var(--ease-out),
			background-color var(--dur-fast) var(--ease-out);
	}
	a:hover {
		background: var(--surface-2);
		color: var(--text-primary);
	}
	a[aria-current='page'] {
		background: var(--accent-soft);
		color: var(--accent);
		/* Inset au lieu d'un vrai border-left : même indicateur "liseré
		   d'accent" que Grafana (seul élément coloré de toute la nav, le
		   reste reste gris/texte), sans décaler le contenu ni casser le
		   box-model du padding existant. */
		box-shadow: inset 3px 0 0 var(--accent);
	}
	.bottom {
		margin-top: auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}
	.conn-row {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		padding: 0 var(--space-1);
	}
	.conn-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		flex-shrink: 0;
		background: var(--text-muted);
	}
	.conn-dot[data-connected='true'] {
		background: var(--good);
		box-shadow: 0 0 0 2px var(--good-soft);
	}
	.refresh-btn {
		flex: 1;
	}
	.refresh-icon {
		display: inline-flex;
	}
	.refresh-icon.spin {
		animation: refresh-spin 0.6s var(--ease-out);
	}
	@keyframes refresh-spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
	.presence {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-2) var(--space-3);
		font-size: var(--text-xs);
		font-weight: 600;
		color: var(--text-muted);
	}
	.presence :global(svg) {
		color: var(--good);
	}
	.ghost-btn {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		padding: var(--space-2) var(--space-3);
		border-radius: var(--radius-sm);
		border: none;
		background: none;
		color: var(--text-muted);
		font-size: var(--text-sm);
		font-weight: 600;
		cursor: pointer;
		text-align: left;
		transition:
			border-color var(--dur-fast) var(--ease-out),
			color var(--dur-fast) var(--ease-out),
			background-color var(--dur-fast) var(--ease-out);
	}
	.ghost-btn:hover {
		background: var(--surface-2);
		color: var(--text-primary);
	}

	@media (max-width: 880px) {
		.sidebar {
			width: 100%;
			flex-direction: row;
			align-items: center;
			padding: var(--space-2) var(--space-3);
			gap: var(--space-4);
			overflow-x: auto;
		}
		.brand span,
		.ghost-btn span {
			display: none;
		}
		.search-pill span,
		.search-pill kbd {
			display: none;
		}
		.search-pill {
			width: auto;
		}
		.quick-wrap {
			display: none;
		}
		ul {
			flex-direction: row;
		}
		.bottom {
			margin-top: 0;
			margin-left: auto;
			flex-direction: row;
		}
		.conn-row {
			padding: 0;
		}
		.refresh-btn span:last-child {
			display: none;
		}
		.presence span:last-child {
			display: none;
		}
	}
</style>
